export function renderBarChart(container, config) {
  const d3 = window.d3;
  if (!d3 || !config.data || !config.data.labels || !config.data.series.length)
    return;

  const horizontal = config.barHorizontal || false;

  const labels = config.data.labels;
  const seriesArray =
    config.visibleSeries && config.visibleSeries.length > 0
      ? config.data.series.filter((s) => config.visibleSeries.includes(s.name))
      : config.data.series;

  const margin = {
    top: config.title ? config.margin.top * 0.35 : config.margin.top * 0.2,
    right: config.margin.right * 0.2,
    bottom:
      (horizontal ? config.yTitle : config.xTitle) || config.legend
        ? config.margin.bottom * 0.6
        : config.margin.bottom * 0.5,
    left: (horizontal ? config.xTitle : config.yTitle)
      ? config.margin.left * 0.5
      : config.margin.left * 0.3,
  };
  if (config.legend && (horizontal ? config.yTitle : config.xTitle))
    margin.bottom += 20;
  if (config.subtitle) margin.top += config.margin.top * 0.1;
  const width = config.width;
  const height = config.height;
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  // Clear previous chart
  const containerSelection = d3.select(container);
  containerSelection.selectAll('*').remove();

  const svg = containerSelection
    .append('svg')
    .attr('viewBox', [0, 0, width, height])
    .append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`);

  // Scales
  let allValues;
  if (config.barStacked) {
    const stackedTotals = labels.map((_, i) =>
      d3.sum(seriesArray.map((s) => s.values[i]))
    );
    allValues = stackedTotals;
  } else {
    allValues = seriesArray.flatMap((s) => s.values);
  }
  const categoryScale = d3
    .scaleBand()
    .domain(labels)
    .range([0, horizontal ? innerHeight : innerWidth])
    .padding(0.2);

  const subCategoryScale = d3
    .scaleBand()
    .domain(seriesArray.map((s) => s.name))
    .range([0, categoryScale.bandwidth()])
    .padding(0.05);

  const valueScale = d3
    .scaleLinear()
    .domain([Math.min(0, d3.min(allValues)), d3.max(allValues)])
    .nice()
    .range(horizontal ? [0, innerWidth] : [innerHeight, 0]);

  // Axes
  if (config.xAxis) {
    const categoryAxis = svg.append('g');
    if (!horizontal) {
      categoryAxis
        .attr('transform', `translate(0,${innerHeight})`)
        .call(d3.axisBottom(categoryScale));
    } else {
      categoryAxis.call(d3.axisLeft(categoryScale));
    }
    categoryAxis
      .selectAll('text')
      .attr(
        'transform',
        `translate(${config.xLabelOffset},0)rotate(${config.xLabelAngle})`
      )
      .style('text-anchor', config.xLabelAnchor)
      .raise();
  }
  if (config.yAxis) {
    const valueAxis = svg.append('g');
    if (!horizontal) {
      valueAxis.call(d3.axisLeft(valueScale).ticks(config.yTicks));
    } else {
      valueAxis
        .attr('transform', `translate(0,${innerHeight})`)
        .call(d3.axisBottom(valueScale).ticks(config.yTicks));
    }
    valueAxis
      .selectAll('text')
      .attr(
        'transform',
        `translate(0,${config.yLabelOffset})rotate(${config.yLabelAngle})`
      )
      .style('text-anchor', config.yLabelAnchor);
  }

  // Gridlines
  if (config.grid) {
    svg
      .append('g')
      .attr('class', 'grid')
      .selectAll('line')
      .data(valueScale.ticks(config.gridTicks))
      .join('line')
      .filter((d) => d !== valueScale.domain()[0])
      .attr('stroke', '#e0e0e0')
      .attr('stroke-dasharray', '2,2')
      .attr(horizontal ? 'y1' : 'x1', 0)
      .attr(horizontal ? 'y2' : 'x2', horizontal ? innerHeight : innerWidth)
      .attr(horizontal ? 'x1' : 'y1', (d) => valueScale(d))
      .attr(horizontal ? 'x2' : 'y2', (d) => valueScale(d));
  }

  // Color scale
  const color = d3
    .scaleOrdinal(d3.schemeCategory10)
    .domain(seriesArray.map((s) => s.name));

  // Tooltip
  const tooltip = d3
    .select('body')
    .append('div')
    .style('position', 'absolute')
    .style('background', 'white')
    .style('border', '1px solid #ccc')
    .style('padding', '4px 8px')
    .style('border-radius', '4px')
    .style('pointer-events', 'none')
    .style('font-size', '12px')
    .style('display', 'none')
    .style('z-index', '9999');

  // Draw bars
  const groups = svg
    .selectAll('.bar-group')
    .data(labels)
    .join('g')
    .attr('class', 'bar-group')
    .attr(
      'transform',
      (d) =>
        `translate(${horizontal ? 0 : categoryScale(d)},${
          horizontal ? categoryScale(d) : 0
        })`
    )
    .attr('pointer-events', 'visiblePainted');

  if (!config.barStacked) {
    // Grouped bar logic
    groups
      .selectAll('rect')
      .data((label, i) =>
        seriesArray.map((s) => ({ name: s.name, value: s.values[i] }))
      )
      .join('rect')
      .attr(horizontal ? 'y' : 'x', (d) => subCategoryScale(d.name))
      .attr(horizontal ? 'x' : 'y', (d) =>
        valueScale((horizontal ? Math.min : Math.max)(0, d.value))
      )
      .attr(horizontal ? 'height' : 'width', subCategoryScale.bandwidth())
      .attr(horizontal ? 'width' : 'height', (d) =>
        Math.abs(valueScale(d.value) - valueScale(0))
      )
      .attr('fill', (d) => color(d.name));
  } else {
    // Stacked bar logic
    groups.each(function (label, i) {
      const g = d3.select(this);
      let cumulativePos = 0;
      let cumulativeNeg = 0;

      seriesArray.forEach((s) => {
        const value = s.values[i];
        const isPositive = value >= 0;

        // Choose correct starting point depending on sign
        const start = isPositive ? cumulativePos : cumulativeNeg;
        const end = start + value;

        // Update cumulative tracker
        if (isPositive) cumulativePos = end;
        else cumulativeNeg = end;

        if (!horizontal) {
          // Vertical stacking
          g.append('rect')
            .attr('x', 0)
            .attr('y', valueScale(Math.max(start, end)))
            .attr('width', categoryScale.bandwidth())
            .attr('height', Math.abs(valueScale(start) - valueScale(end)))
            .attr('fill', color(s.name));
        } else {
          // Horizontal stacking
          g.append('rect')
            .attr('y', 0)
            .attr('x', valueScale(Math.min(start, end)))
            .attr('height', categoryScale.bandwidth())
            .attr('width', Math.abs(valueScale(start) - valueScale(end)))
            .attr('fill', color(s.name));
        }
      });
    });
  }

  if (config.tooltip) {
    groups
      .selectAll('rect')
      .data((label, i) =>
        seriesArray.map((s) => ({ name: s.name, value: s.values[i] }))
      )
      .on('mouseover', (event, d) =>
        tooltip.style('display', 'block').html(`${d.name}: ${d.value}`)
      )
      .on('mousemove', (event) =>
        tooltip
          .style('left', event.pageX + 10 + 'px')
          .style('top', event.pageY - 20 + 'px')
      )
      .on('mouseout', () => tooltip.style('display', 'none'));
  }

  if (config.title) {
    svg
      .append('text')
      .attr('x', innerWidth / 2)
      .attr('y', -margin.top / 2)
      .attr('text-anchor', 'middle')
      .style('font-size', '16px')
      .text(config.title);
  }

  if (config.subtitle) {
    svg
      .append('text')
      .attr('x', innerWidth / 2)
      .attr('y', -margin.top / 2 + 15)
      .attr('text-anchor', 'middle')
      .style('font-size', '12px')
      .style('fill', '#666')
      .text(config.subtitle);
  }

  if (config.xTitle) {
    const xTitleSelection = svg.append('text');
    if (!horizontal) {
      xTitleSelection
        .attr('x', innerWidth / 2)
        .attr('y', innerHeight + margin.bottom - (config.legend ? 25 : 5))
        .attr('text-anchor', 'middle')
        .style('font-size', '10px')
        .style('fill', '#666')
        .text(config.xTitle);
    } else {
      xTitleSelection
        .attr('transform', 'rotate(-90)')
        .attr('x', -innerHeight / 2)
        .attr('y', -margin.left + 20)
        .attr('text-anchor', 'middle')
        .style('font-size', '12px')
        .style('fill', '#666')
        .text(config.xTitle);
    }
  }

  if (config.yTitle) {
    const yTitleSelection = svg.append('text');
    if (!horizontal) {
      yTitleSelection
        .attr('transform', 'rotate(-90)')
        .attr('x', -innerHeight / 2)
        .attr('y', -margin.left + 20)
        .attr('text-anchor', 'middle')
        .style('font-size', '12px')
        .style('fill', '#666')
        .text(config.yTitle);
    } else {
      yTitleSelection
        .attr('x', innerWidth / 2)
        .attr('y', innerHeight + margin.bottom - (config.legend ? 25 : 5))
        .attr('text-anchor', 'middle')
        .style('font-size', '10px')
        .style('fill', '#666')
        .text(config.yTitle);
    }
  }

  // Legend
  if (config.legend) {
    const legend = svg
      .append('g')
      .attr(
        'transform',
        `translate(0, ${innerHeight + margin.bottom - ((horizontal ? config.yTitle : config.xTitle) ? 20 : 15)})`
      );

    seriesArray.forEach((series, i) => {
      const legendGroup = legend
        .append('g')
        .attr('transform', `translate(${i * 60}, 0)`);
      legendGroup
        .append('rect')
        .attr('width', 15)
        .attr('height', 15)
        .attr('fill', color(series.name));
      legendGroup
        .append('text')
        .attr('x', 20)
        .attr('y', 12)
        .style('font-size', '12px')
        .text(series.name);
    });
  }
}
